import { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate, Link } from 'react-router-dom';
import { Button, IWarning, FormInput, FormInputTypes, Form } from '@praktio/common-ui';
import { cloneDeep } from 'lodash';
import { Helmet } from 'react-helmet';
import { Form as RBForm } from 'react-bootstrap';

import { RootState } from '../../ducks/store';
import { signupUser, signupAuthedUser } from '../../ducks/users';
import { findFormErrors, handleUserFormEdit, useQuery } from '../../utils/functions';
import UnauthenticatedWrapper from '../common/UnauthenticatedWrapper/UnauthenticatedWrapper';
import ShowHidePasswordButton from '../common/ShowHidePasswordButton/ShowHidePasswordButton';
import { SiteKey } from '../../utils/constants';

import './SelfSignup.scss';
import OverlaySpinner from '../common/OverlaySpinner/OverlaySpinner';

const SelfSignup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();
  const code = query.get('code') || undefined;
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const { isAuthenticated } = useSelector((state: RootState) => {
    return {
      isAuthenticated: state.auth.isAuthenticated,
    };
  });

  const [user, setUser] = useState<IUser>({
    email: '',
    password: '',
    confirmPassword: '',
    code: code || '',
    token: '',
  });
  const [userFormErrors, setUserFormErrors] = useState<IBootstrapFormErrors>();
  const [warnings, setWarnings] = useState<IWarning[]>([]);
  const [passwordFieldType, setPasswordFieldType] = useState<FormInputTypes>('password');
  const [disableSave, setDisableSave] = useState(false);

  // We only want this effect to happen on the initial load, which
  // assumes the user is already authenticated
  useEffect(() => {
    if (code && isAuthenticated) {
      dispatch(signupAuthedUser(code, navigate));
    }
  }, []);

  const togglePassword = () => {
    setPasswordFieldType(passwordFieldType === 'password' ? 'text' : 'password');
  };

  const prepAndExecuteUserFormEdit = (
    field: string,
    value: string | boolean | ICLEEntry | string[],
  ) => {
    const clonedUser = cloneDeep(user);
    const clonedFormErrors = cloneDeep(userFormErrors);
    const clonedWarnings = cloneDeep(warnings);

    handleUserFormEdit({ field, value, clonedUser, clonedFormErrors, clonedWarnings });

    // Queue/Execute setStates.
    setUser(clonedUser);
    setUserFormErrors(clonedFormErrors);
    setWarnings(clonedWarnings);
  };

  const handleSubmit = async (e?: React.MouseEvent<HTMLElement>) => {
    e?.preventDefault();
    setDisableSave(true);
    const newErrors = await findFormErrors({
      user: user,
      requirePassword: true,
      ignoreCurrentPassword: true,
      isSelfSignup: true,
    });
    if (Object.keys(newErrors).length > 0) {
      setUserFormErrors(newErrors);
    } else {
      dispatch(signupUser(user, navigate));
      recaptchaRef.current?.reset();
    }
    setDisableSave(false);
  };

  if (code && isAuthenticated) {
    return <OverlaySpinner />;
  }

  return (
    <UnauthenticatedWrapper formName="Sign Up with Code">
      <Helmet>
        <title>Self Signup - Praktio</title>
      </Helmet>
      <Form
        formId="self-signup-form"
        className="self-signup-form"
        onSubmit={(e: any) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div className="SelfSignup__returning-user">
          <span className="SelfSignup__question-label">Returning Praktio user?</span> To redeem a
          code for new courses, input your current password in both the "Password" and "Confirm
          Password" fields. Otherwise, use the "log in" button below to sign in with your
          credentials.
        </div>
        <div className="SelfSignup__returning-links">
          <Button onClick={() => navigate('/login')} btnStyle="secondary" className="secondary">
            LOG IN
          </Button>
          <Link className="SelfSignup__forgot" to="/forgot">
            Forgot password?
          </Link>
        </div>
        <div className="SelfSignup__new-user">
          <span className="SelfSignup__question-label">New Praktio user?</span> Create an account
          here.
        </div>
        <FormInput
          value={user?.email}
          label="Email"
          placeholder="Email"
          fieldName="email"
          onChange={(e: any) => prepAndExecuteUserFormEdit('email', e.target.value)}
          error={userFormErrors?.email}
          required
        />
        <FormInput
          value={user?.password}
          label="Password"
          placeholder="Password"
          fieldName="password"
          type={passwordFieldType}
          onChange={(e: any) => prepAndExecuteUserFormEdit('password', e.target.value)}
          error={userFormErrors?.password}
          required
          labelWrapperClassName="d-flex justify-content-between"
          labelAdditions={
            <ShowHidePasswordButton
              clickFunction={togglePassword}
              passwordVisible={passwordFieldType === 'text'}
              passwordFieldType={passwordFieldType}
            />
          }
          noTrimming
        />
        <FormInput
          value={user?.confirmPassword}
          label="Confirm Password"
          placeholder="Confirm Password"
          fieldName="confirmPassword"
          type={passwordFieldType}
          onChange={(e: any) => prepAndExecuteUserFormEdit('confirmPassword', e.target.value)}
          error={userFormErrors?.confirmPassword}
          required
          noTrimming
        />
        <FormInput
          value={user?.code}
          label="Signup Code"
          placeholder="Signup Code"
          fieldName="code"
          onChange={(e: any) => prepAndExecuteUserFormEdit('code', e.target.value)}
          error={userFormErrors?.code}
          required
        />
        <RBForm.Group className="SelfSignupCaptcha mb-3">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={SiteKey}
            onChange={(token) => {
              prepAndExecuteUserFormEdit('token', token as string);
            }}
          />
          <RBForm.Control className="d-none" isInvalid={!!userFormErrors?.token} />
          <RBForm.Control.Feedback type="invalid">{userFormErrors?.token}</RBForm.Control.Feedback>
        </RBForm.Group>
        <div className="d-flex justify-content-between">
          <Button
            onClick={async (e: any) => await handleSubmit(e)}
            form="self-signup-form"
            type="submit"
            btnStyle="primary"
            className="form-submit-button"
            disabled={!!disableSave}
          >
            SUBMIT
          </Button>
          <div className="text-left">
            <span className="required-notice">* required</span>
          </div>
        </div>
        <div className="form-footer">
          By submitting, you agree to Praktio's{' '}
          <a href="https://praktio.com/pages/terms-of-service">Terms of Service</a>, along with our{' '}
          <a href="https://praktio.com/pages/privacy-policy">Privacy Policy</a>. Our site uses
          cookies, as further detailed in our Privacy Policy, and you consent to such use by
          submitting.
        </div>
      </Form>
    </UnauthenticatedWrapper>
  );
};

export default SelfSignup;
